import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import { Plus, Edit2, X, Search, ArrowLeft, GripVertical } from "lucide-react";
import varifiedIcon from "../../../assests/verifiedIcon.svg";
import { toastr } from "react-redux-toastr";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./CategoryAttributes.css";

// Sortable Heading Header Component
const SortableHeadingHeader = ({ heading, attributesCount, onEdit, onAddAttribute, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: heading.id || heading.heading });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="cat-attr-heading-header">
      <div className="cat-attr-heading-title">
        <div className="cat-attr-drag-handle-wrapper" {...attributes} {...listeners}>
          <GripVertical size={18} className="cat-attr-drag-handle-icon" />
        </div>
        <div className="cat-attr-heading-border">
          <h2 className="cat-attr-heading-text">{heading.heading}</h2>
        </div>
        <span className="cat-attr-level-badge cat-attr-level-cat">CAT LEVEL</span>
        <button 
          className="cat-attr-edit-heading-btn" 
          onClick={() => onEdit(heading)} 
          title="Edit Heading"
        >
          <Edit2 size={14} />
        </button>
      </div>
      <div className="cat-attr-heading-actions">
        <button className="cat-attr-add-attribute-btn" onClick={() => onAddAttribute(heading)} title="Add Attribute">
          <Plus size={14} /> Add Attribute
        </button>
      </div>
    </div>
  );
};

const CategoryAttributesComponent = ({
  attributeHeadings = [],
  categoryName,
  loading,
  searchQuery,
  setSearchQuery,
  isSubmitting,
  onEditHeading,
  onBack,
  onAddAttribute,
  onEditAttribute,
  onDeleteAttribute,
  onDragEndHeadings,
  onAddHeadingClick
}) => {
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [selectedHeadingForAttribute, setSelectedHeadingForAttribute] = useState(null);
  const [newAttributeData, setNewAttributeData] = useState({
    key: "",
    dataType: "STRING",
    isRequired: "No",
    unitType: "No",
    dropdownOptions: [],
    isBoolean: "",
    unitValues: ""
  });
  const [dropdownInput, setDropdownInput] = useState("");
  const [editHeadingName, setEditHeadingName] = useState("");
  const [showEditHeadingModal, setShowEditHeadingModal] = useState(false);
  const [selectedHeadingForEdit, setSelectedHeadingForEdit] = useState(null);
  
  const [showEditAttributeModal, setShowEditAttributeModal] = useState(false);
  const [editAttributeData, setEditAttributeData] = useState({
    key: "",
    dataType: "STRING",
    isRequired: "No",
    unitType: "No",
    dropdownOptions: [],
    isBoolean: "",
    unitValues: ""
  });
  const [editDropdownInput, setEditDropdownInput] = useState("");
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [editingHeadingForAttr, setEditingHeadingForAttr] = useState(null);
  
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [headingForDelete, setHeadingForDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  
  const [showOperationSuccessPopup, setShowOperationSuccessPopup] = useState(false);
  const [operationSuccessMessage, setOperationSuccessMessage] = useState("");
  const [operationSuccessTitle, setOperationSuccessTitle] = useState("");

  const dataTypeOptions = [
    { value: "STRING", label: "Text" },
    { value: "NUMBER", label: "Number" },
    { value: "DROPDOWN", label: "List" },
    { value: "BOOLEAN", label: "Boolean" },
  ];

  const isUnitAndRequiredEnabled = (dataType) => {
    return dataType === "DROPDOWN";
  };

  const getDataTypeBadgeClass = (dataType) => {
    switch (dataType) {
      case "STRING": return "cat-attr-badge-string";
      case "NUMBER": return "cat-attr-badge-number";
      case "DROPDOWN": return "cat-attr-badge-dropdown";
      case "BOOLEAN": return "cat-attr-badge-boolean";
      default: return "cat-attr-badge-string";
    }
  };

  const getDisplayLabel = (dataType) => {
    if (dataType === "DROPDOWN") return "List";
    switch (dataType) {
      case "STRING": return "Text";
      case "NUMBER": return "Number";
      case "BOOLEAN": return "Boolean";
      default: return dataType;
    }
  };

  // ✅ Get Unit Display based on unitValues
  const getUnitDisplay = (attr) => {
    // For DROPDOWN type
    if (attr.dataType === "DROPDOWN") {
      // Check if unitValues exists and has at least one value
      if (attr.unitValues && Array.isArray(attr.unitValues) && attr.unitValues.length > 0) {
        return {
          label: "Yes",
          className: "cat-attr-unit-yes",
          values: attr.unitValues.join(', ')
        };
      }
      return {
        label: "No",
        className: "cat-attr-unit-no",
        values: null
      };
    }
    // For non-DROPDOWN types
    return {
      label: attr.unit || "No",
      className: attr.unit === "Yes" ? 'cat-attr-unit-yes' : 'cat-attr-unit-no',
      values: null
    };
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const addDropdownOption = () => {
    if (dropdownInput.trim() && !newAttributeData.dropdownOptions.includes(dropdownInput.trim())) {
      setNewAttributeData(prev => ({
        ...prev,
        dropdownOptions: [...prev.dropdownOptions, dropdownInput.trim()]
      }));
      setDropdownInput("");
    }
  };

  const removeDropdownOption = (option) => {
    setNewAttributeData(prev => ({
      ...prev,
      dropdownOptions: prev.dropdownOptions.filter(opt => opt !== option)
    }));
  };

  const addEditDropdownOption = () => {
    if (editDropdownInput.trim() && !editAttributeData.dropdownOptions.includes(editDropdownInput.trim())) {
      setEditAttributeData(prev => ({
        ...prev,
        dropdownOptions: [...prev.dropdownOptions, editDropdownInput.trim()]
      }));
      setEditDropdownInput("");
    }
  };

  const removeEditDropdownOption = (option) => {
    setEditAttributeData(prev => ({
      ...prev,
      dropdownOptions: prev.dropdownOptions.filter(opt => opt !== option)
    }));
  };

  const handleAddAttributeSubmit = () => {
    if (!newAttributeData.key.trim()) {
      toastr.warning("Warning", "Please enter attribute key");
      return;
    }
    
    if (newAttributeData.dataType === "BOOLEAN" && !newAttributeData.isBoolean) {
      toastr.warning("Warning", "Please select Yes/No for Boolean attribute");
      return;
    }
    
    if (newAttributeData.dataType === "DROPDOWN" && newAttributeData.unitType === "Yes" && !newAttributeData.unitValues.trim()) {
      toastr.warning("Warning", "Please enter unit values");
      return;
    }
    
    onAddAttribute(selectedHeadingForAttribute, newAttributeData, 
      (attributeName) => {
        setOperationSuccessTitle("Attribute Added");
        setOperationSuccessMessage(`"${attributeName}" has been added successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Add Failed");
        setPopupMessage(errorMsg || "Failed to add attribute. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowAddAttributeModal(false);
    setSelectedHeadingForAttribute(null);
    setNewAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setDropdownInput("");
  };

  const handleEditAttributeSubmit = () => {
    if (!editAttributeData.key.trim()) {
      toastr.warning("Warning", "Please enter attribute key");
      return;
    }
    
    if (editAttributeData.dataType === "BOOLEAN" && !editAttributeData.isBoolean) {
      toastr.warning("Warning", "Please select Yes/No for Boolean attribute");
      return;
    }
    
    if (editAttributeData.dataType === "DROPDOWN" && editAttributeData.unitType === "Yes" && !editAttributeData.unitValues.trim()) {
      toastr.warning("Warning", "Please enter unit values");
      return;
    }
    
    onEditAttribute(editingHeadingForAttr, editingAttribute, editAttributeData,
      (attributeName) => {
        setOperationSuccessTitle("Attribute Updated");
        setOperationSuccessMessage(`"${attributeName}" has been updated successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Update Failed");
        setPopupMessage(errorMsg || "Failed to update attribute. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowEditAttributeModal(false);
    setEditingAttribute(null);
    setEditingHeadingForAttr(null);
    setEditAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setEditDropdownInput("");
  };

  const openAddAttributeModal = (heading) => {
    setSelectedHeadingForAttribute(heading);
    setNewAttributeData({ key: "", dataType: "STRING", isRequired: "No", unitType: "No", dropdownOptions: [], isBoolean: "", unitValues: "" });
    setDropdownInput("");
    setShowAddAttributeModal(true);
  };

  const openEditAttributeModal = (heading, attribute) => {
    setEditingHeadingForAttr(heading);
    setEditingAttribute(attribute);
    
    // ✅ Check unitValues to set unitType
    let unitTypeValue = "No";
    if (attribute.dataType === "DROPDOWN") {
      if (attribute.unitValues && Array.isArray(attribute.unitValues) && attribute.unitValues.length > 0) {
        unitTypeValue = "Yes";
      } else {
        unitTypeValue = "No";
      }
    } else {
      unitTypeValue = attribute.unit === "Yes" ? "Yes" : "No";
    }
    
    setEditAttributeData({
      key: attribute.key,
      dataType: attribute.dataType,
      isRequired: attribute.isRequired === true ? "Yes" : "No",
      unitType: unitTypeValue,
      dropdownOptions: attribute.dropdownOptions || [],
      isBoolean: attribute.isBoolean || "",
      unitValues: attribute.unitValues && Array.isArray(attribute.unitValues) 
        ? attribute.unitValues.join(', ') 
        : (attribute.unitValues || "")
    });
    setEditDropdownInput("");
    setShowEditAttributeModal(true);
  };

  const openEditHeadingModal = (heading) => {
    setSelectedHeadingForEdit(heading);
    setEditHeadingName(heading.heading);
    setShowEditHeadingModal(true);
  };

  const handleEditHeadingSubmit = () => {
    if (!editHeadingName.trim()) {
      toastr.warning("Warning", "Please enter heading name");
      return;
    }
    onEditHeading(selectedHeadingForEdit, editHeadingName.trim(),
      (headingName) => {
        setOperationSuccessTitle("Heading Updated");
        setOperationSuccessMessage(`Heading updated to "${headingName}" successfully!`);
        setShowOperationSuccessPopup(true);
      },
      (errorMsg) => {
        setPopupTitle("Update Failed");
        setPopupMessage(errorMsg || "Failed to update heading. Please try again.");
        setShowErrorPopup(true);
      }
    );
    setShowEditHeadingModal(false);
    setSelectedHeadingForEdit(null);
    setEditHeadingName("");
  };

  const handleDeleteClick = (heading, attribute) => {
    setAttributeToDelete(attribute);
    setHeadingForDelete(heading);
    setShowDeleteConfirmPopup(true);
  };

  const handleConfirmDelete = () => {
    if (attributeToDelete && headingForDelete) {
      setIsDeleting(true);
      setShowDeleteConfirmPopup(false);
      
      onDeleteAttribute(
        headingForDelete, 
        attributeToDelete,
        (deletedKey) => {
          setIsDeleting(false);
          setOperationSuccessTitle("Attribute Deleted");
          setOperationSuccessMessage(`"${deletedKey}" has been removed successfully!`);
          setShowOperationSuccessPopup(true);
          setAttributeToDelete(null);
          setHeadingForDelete(null);
        },
        (errorMsg) => {
          setIsDeleting(false);
          setPopupTitle("Delete Failed");
          setPopupMessage(errorMsg || "Failed to delete attribute. Please try again.");
          setShowErrorPopup(true);
          setAttributeToDelete(null);
          setHeadingForDelete(null);
        }
      );
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmPopup(false);
    setAttributeToDelete(null);
    setHeadingForDelete(null);
  };

  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setPopupMessage("");
    setPopupTitle("");
  };

  const handleCloseOperationSuccessPopup = () => {
    setShowOperationSuccessPopup(false);
    setOperationSuccessMessage("");
    setOperationSuccessTitle("");
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = attributeHeadings.findIndex(h => (h.id || h.heading) === active.id);
      const newIndex = attributeHeadings.findIndex(h => (h.id || h.heading) === over.id);
      onDragEndHeadings(oldIndex, newIndex);
    }
  };

  if (loading) {
    return (
      <div className="cat-attr-container">
        <Sidebar />
        <div className="cat-attr-loading">
          <div className="cat-attr-spinner"></div>
          <p className="cat-attr-loading-text">Loading attributes...</p>
        </div>
      </div>
    );
  }

  const headingsWithIds = attributeHeadings.map((heading, idx) => ({
    ...heading,
    id: heading.id || heading.heading,
  }));

  const filteredHeadings = headingsWithIds.filter(heading => {
    if (!searchQuery) return true;
    const headingMatch = heading.heading.toLowerCase().includes(searchQuery.toLowerCase());
    const attributesMatch = (heading.productAttributeDtoList || []).some(attr =>
      attr.key.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return headingMatch || attributesMatch;
  });

  return (
    <div className="cat-attr-container">
      <Sidebar />
      <div className="cat-attr-content">
        {/* Header */}
        <div className="cat-attr-header">
          <button className="cat-attr-back-btn" onClick={onBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="cat-attr-header-info">
            <h1 className="cat-attr-header-title">Category Attributes</h1>
            <div className="cat-attr-badge">Parent Category: {categoryName}</div>
          </div>
          <button className="cat-attr-add-heading-btn" onClick={onAddHeadingClick}>
            <Plus size={18} /> Add Attribute Category
          </button>
        </div>

        {/* Search */}
        <div className="cat-attr-search-wrapper">
          <Search size={18} className="cat-attr-search-icon" />
          <input
            type="text"
            placeholder="Search Attributes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cat-attr-search-input"
          />
          {searchQuery && (
            <button 
              className="cat-attr-search-clear-btn"
              onClick={() => setSearchQuery("")}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* SCROLLABLE CONTAINER */}
        <div className="cat-attr-scrollable-container">
          <div className="cat-attr-headings-list">
            {filteredHeadings.length === 0 && (
              <div className="cat-attr-empty-state">
                <div className="cat-attr-empty-icon">📋</div>
                <h3 className="cat-attr-empty-title">No Attribute Headings Found</h3>
                <p className="cat-attr-empty-description">Click "Add Attribute Category" to create your first heading.</p>
              </div>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredHeadings.map(h => h.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredHeadings.map((heading, idx) => {
                  const attributes = heading.productAttributeDtoList || [];
                  const filteredAttributes = searchQuery
                    ? attributes.filter(attr => attr.key.toLowerCase().includes(searchQuery.toLowerCase()))
                    : attributes;

                  return (
                    <div key={heading.id} className="cat-attr-heading-card">
                      <SortableHeadingHeader
                        heading={heading}
                        attributesCount={filteredAttributes.length}
                        onEdit={openEditHeadingModal}
                        onAddAttribute={openAddAttributeModal}
                        index={idx}
                      />

                      <div className="cat-attr-attributes-list">
                        {filteredAttributes.length === 0 ? (
                          <div className="cat-attr-no-attributes">
                            <p className="cat-attr-no-attributes-text">No attributes in this heading.</p>
                            <button className="cat-attr-add-attribute-empty-btn" onClick={() => openAddAttributeModal(heading)}>
                              <Plus size={14} /> Add Attribute
                            </button>
                          </div>
                        ) : (
                          <div className="cat-attr-table-responsive">
                            <table className="cat-attr-table">
                              <thead>
                                <tr className="cat-attr-table-header-row">
                                  <th className="cat-attr-table-header cat-attr-priority-col">S.No.</th>
                                  <th className="cat-attr-table-header">ATTRIBUTE NAME</th>
                                  <th className="cat-attr-table-header">VALUE TYPE</th>
                                  <th className="cat-attr-table-header">UNIT TYPE</th>
                                  <th className="cat-attr-table-header">REQUIRED</th>
                                  <th className="cat-attr-table-header cat-attr-action-col">ACTION</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredAttributes.map((attr, attrIdx) => {
                                  const unitInfo = getUnitDisplay(attr);
                                  return (
                                    <tr key={attr.id || attrIdx} className="cat-attr-table-row">
                                      <td className="cat-attr-table-cell cat-attr-priority-cell">{attrIdx + 1}</td>
                                      <td className="cat-attr-table-cell cat-attr-attr-key">{attr.key}</td>
                                      <td className="cat-attr-table-cell cat-attr-value-type-cell">
                                        <span className={`cat-attr-data-type-badge ${getDataTypeBadgeClass(attr.dataType)}`}>
                                          {getDisplayLabel(attr.dataType)}
                                        </span>
                                      </td>
                                      <td className="cat-attr-table-cell cat-attr-unit-cell">
                                        <span className={`cat-attr-unit-badge ${unitInfo.className}`}>
                                          {unitInfo.label}
                                         
                                        </span>
                                      </td>
                                      <td className="cat-attr-table-cell cat-attr-required-cell">
                                        <span className={`cat-attr-required-badge ${attr.isRequired ? 'cat-attr-required-yes' : 'cat-attr-required-no'}`}>
                                          {attr.isRequired ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td className="cat-attr-table-cell cat-attr-action-cell">
                                        <div className="cat-attr-action-buttons">
                                          <button 
                                            className="cat-attr-edit-attr-btn" 
                                            onClick={() => openEditAttributeModal(heading, attr)} 
                                            title="Edit Attribute"
                                          >
                                            <Edit2 size={14} />
                                          </button>
                                          <button 
                                            className="cat-attr-delete-attr-btn" 
                                            onClick={() => handleDeleteClick(heading, attr)} 
                                            title="Delete Attribute"
                                            disabled={isDeleting}
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      {/* ADD ATTRIBUTE MODAL */}
      {showAddAttributeModal && selectedHeadingForAttribute && (
        <div className="cat-attr-modal-overlay" onClick={() => setShowAddAttributeModal(false)}>
          <div className="cat-attr-modal cat-attr-add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-modal-header">
              <h2 className="cat-attr-modal-title">Add Attribute to "{selectedHeadingForAttribute.heading}"</h2>
              <button className="cat-attr-modal-close-btn" onClick={() => setShowAddAttributeModal(false)}><X size={20} /></button>
            </div>
            <div className="cat-attr-modal-body">
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Attribute Name <span className="cat-attr-required-star">*</span></label>
                <input
                  type="text"
                  className="cat-attr-form-input"
                  placeholder="e.g., Screen Type, Processor, RAM"
                  value={newAttributeData.key}
                  onChange={(e) => setNewAttributeData(prev => ({ ...prev, key: e.target.value }))}
                />
              </div>
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Value Type</label>
                <select
                  className="cat-attr-form-select"
                  value={newAttributeData.dataType}
                  onChange={(e) => {
                    const newDataType = e.target.value;
                    setNewAttributeData(prev => ({ 
                      ...prev, 
                      dataType: newDataType, 
                      dropdownOptions: [],
                      isBoolean: "",
                      unitValues: "",
                      unitType: "No",
                      isRequired: "No"
                    }));
                  }}
                >
                  {dataTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* List Values - Only show for DROPDOWN type */}
              {newAttributeData.dataType === "DROPDOWN" && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">List Values <span className="cat-attr-required-star">*</span></label>
                  <div className="cat-attr-dropdown-input-group">
                    <input
                      type="text"
                      className="cat-attr-dropdown-input"
                      placeholder="Enter option"
                      value={dropdownInput}
                      onChange={(e) => setDropdownInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addDropdownOption()}
                    />
                    <button className="cat-attr-dropdown-add-btn" onClick={addDropdownOption}>Add</button>
                  </div>
                  <div className="cat-attr-options-preview">
                    {newAttributeData.dropdownOptions.map((opt, idx) => (
                      <span key={idx} className="cat-attr-option-tag">
                        {opt}
                        <button className="cat-attr-option-remove-btn" onClick={() => removeDropdownOption(opt)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Boolean Values - Only show for BOOLEAN type */}
              {newAttributeData.dataType === "BOOLEAN" && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">Boolean Values <span className="cat-attr-required-star">*</span></label>
                  <div className="cat-attr-radio-group cat-attr-boolean-radio-group">
                    <label className={`cat-attr-radio-label ${newAttributeData.isBoolean === "Yes" ? 'cat-attr-radio-active' : ''}`}>
                      <input 
                        type="radio" 
                        className="cat-attr-radio-input"
                        value="Yes" 
                        checked={newAttributeData.isBoolean === "Yes"} 
                        onChange={() => setNewAttributeData(prev => ({ ...prev, isBoolean: "Yes" }))}
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`cat-attr-radio-label ${newAttributeData.isBoolean === "No" ? 'cat-attr-radio-active' : ''}`}>
                      <input 
                        type="radio" 
                        className="cat-attr-radio-input"
                        value="No" 
                        checked={newAttributeData.isBoolean === "No"} 
                        onChange={() => setNewAttributeData(prev => ({ ...prev, isBoolean: "No" }))}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              )}
              {/* Unit Type - ONLY ENABLED for DROPDOWN type */}
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Unit Type</label>
                <div className="cat-attr-radio-group">
                  <label className={`cat-attr-radio-label ${newAttributeData.unitType === "Yes" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'cat-attr-radio-disabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="Yes" 
                      checked={newAttributeData.unitType === "Yes"} 
                      onChange={() => {
                        setNewAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "Yes"
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`cat-attr-radio-label ${newAttributeData.unitType === "No" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'cat-attr-radio-disabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="No" 
                      checked={newAttributeData.unitType === "No"} 
                      onChange={() => {
                        setNewAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "No",
                          unitValues: ""
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              {/* Unit Values - Only show when Unit Type is Yes AND dataType is DROPDOWN */}
              {newAttributeData.unitType === "Yes" && isUnitAndRequiredEnabled(newAttributeData.dataType) && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">Unit Values <span className="cat-attr-required-star">*</span></label>
                  <input
                    type="text"
                    className="cat-attr-form-input"
                    placeholder="GB, TB, MB, KG, GM"
                    value={newAttributeData.unitValues}
                    onChange={(e) => setNewAttributeData(prev => ({ ...prev, unitValues: e.target.value }))}
                    disabled={isSubmitting}
                  />
                 
                </div>
              )}
              {/* Is Required - ONLY ENABLED for DROPDOWN type */}
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Is Required?</label>
                <div className="cat-attr-radio-group">
                  <label className={`cat-attr-radio-label ${newAttributeData.isRequired === "Yes" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'cat-attr-radio-enabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="Yes" 
                      checked={newAttributeData.isRequired === "Yes"} 
                      onChange={() => setNewAttributeData(prev => ({ ...prev, isRequired: "Yes" }))}
                      // disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`cat-attr-radio-label ${newAttributeData.isRequired === "No" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(newAttributeData.dataType) ? 'cat-attr-radio-enabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="No" 
                      checked={newAttributeData.isRequired === "No"} 
                      onChange={() => setNewAttributeData(prev => ({ ...prev, isRequired: "No" }))}
                      // disabled={!isUnitAndRequiredEnabled(newAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="cat-attr-modal-footer">
              <button className="cat-attr-modal-cancel-btn" onClick={() => setShowAddAttributeModal(false)}>Cancel</button>
              <button 
                className="cat-attr-modal-submit-btn"
                onClick={handleAddAttributeSubmit} 
                disabled={
                  !newAttributeData.key.trim() || 
                  (newAttributeData.dataType === "DROPDOWN" && newAttributeData.dropdownOptions.length === 0) ||
                  (newAttributeData.dataType === "BOOLEAN" && !newAttributeData.isBoolean) ||
                  (newAttributeData.dataType === "DROPDOWN" && newAttributeData.unitType === "Yes" && !newAttributeData.unitValues.trim()) ||
                  isSubmitting
                }
              >
                Add Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ATTRIBUTE MODAL */}
      {showEditAttributeModal && editingAttribute && editingHeadingForAttr && (
        <div className="cat-attr-modal-overlay" onClick={() => setShowEditAttributeModal(false)}>
          <div className="cat-attr-modal cat-attr-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-modal-header">
              <h2 className="cat-attr-modal-title">Edit Attribute</h2>
              <button className="cat-attr-modal-close-btn" onClick={() => setShowEditAttributeModal(false)}><X size={20} /></button>
            </div>
            <div className="cat-attr-modal-body">
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Attribute Name <span className="cat-attr-required-star">*</span></label>
                <input
                  type="text"
                  className="cat-attr-form-input"
                  placeholder="e.g., Screen Type, Processor, RAM"
                  value={editAttributeData.key}
                  onChange={(e) => setEditAttributeData(prev => ({ ...prev, key: e.target.value }))}
                />
              </div>
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Value Type</label>
                <select
                  className="cat-attr-form-select"
                  value={editAttributeData.dataType}
                  onChange={(e) => {
                    const newDataType = e.target.value;
                    setEditAttributeData(prev => ({ 
                      ...prev, 
                      dataType: newDataType, 
                      dropdownOptions: [],
                      isBoolean: "",
                      unitValues: "",
                      unitType: "No",
                      isRequired: "No"
                    }));
                  }}
                >
                  {dataTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* List Values - Only show for DROPDOWN type */}
              {editAttributeData.dataType === "DROPDOWN" && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">List Values <span className="cat-attr-required-star">*</span></label>
                  <div className="cat-attr-dropdown-input-group">
                    <input
                      type="text"
                      className="cat-attr-dropdown-input"
                      placeholder="Enter option"
                      value={editDropdownInput}
                      onChange={(e) => setEditDropdownInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEditDropdownOption()}
                    />
                    <button className="cat-attr-dropdown-add-btn" onClick={addEditDropdownOption}>Add</button>
                  </div>
                  <div className="cat-attr-options-preview">
                    {editAttributeData.dropdownOptions.map((opt, idx) => (
                      <span key={idx} className="cat-attr-option-tag">
                        {opt}
                        <button className="cat-attr-option-remove-btn" onClick={() => removeEditDropdownOption(opt)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Boolean Values - Only show for BOOLEAN type */}
              {editAttributeData.dataType === "BOOLEAN" && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">Boolean Values <span className="cat-attr-required-star">*</span></label>
                  <div className="cat-attr-radio-group cat-attr-boolean-radio-group">
                    <label className={`cat-attr-radio-label ${editAttributeData.isBoolean === "Yes" ? 'cat-attr-radio-active' : ''}`}>
                      <input 
                        type="radio" 
                        className="cat-attr-radio-input"
                        value="Yes" 
                        checked={editAttributeData.isBoolean === "Yes"} 
                        onChange={() => setEditAttributeData(prev => ({ ...prev, isBoolean: "Yes" }))}
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`cat-attr-radio-label ${editAttributeData.isBoolean === "No" ? 'cat-attr-radio-active' : ''}`}>
                      <input 
                        type="radio" 
                        className="cat-attr-radio-input"
                        value="No" 
                        checked={editAttributeData.isBoolean === "No"} 
                        onChange={() => setEditAttributeData(prev => ({ ...prev, isBoolean: "No" }))}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              )}
              {/* Unit Type - ONLY ENABLED for DROPDOWN type */}
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Unit Type</label>
                <div className="cat-attr-radio-group">
                  <label className={`cat-attr-radio-label ${editAttributeData.unitType === "Yes" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'cat-attr-radio-disabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="Yes" 
                      checked={editAttributeData.unitType === "Yes"} 
                      onChange={() => {
                        setEditAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "Yes"
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`cat-attr-radio-label ${editAttributeData.unitType === "No" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'cat-attr-radio-disabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="No" 
                      checked={editAttributeData.unitType === "No"} 
                      onChange={() => {
                        setEditAttributeData(prev => ({ 
                          ...prev, 
                          unitType: "No",
                          unitValues: ""
                        }));
                      }}
                      disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              {/* Unit Values - Only show when Unit Type is Yes AND dataType is DROPDOWN */}
              {editAttributeData.unitType === "Yes" && isUnitAndRequiredEnabled(editAttributeData.dataType) && (
                <div className="cat-attr-form-group">
                  <label className="cat-attr-form-label">Unit Values <span className="cat-attr-required-star">*</span></label>
                  <input
                    type="text"
                    className="cat-attr-form-input"
                    placeholder="GB, TB, MB, KG, GM"
                    value={editAttributeData.unitValues}
                    onChange={(e) => setEditAttributeData(prev => ({ ...prev, unitValues: e.target.value }))}
                    disabled={isSubmitting}
                  />
                 
                </div>
              )}
              {/* Is Required - ONLY ENABLED for DROPDOWN type */}
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Is Required?</label>
                <div className="cat-attr-radio-group">
                  <label className={`cat-attr-radio-label ${editAttributeData.isRequired === "Yes" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'cat-attr-radio-enabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="Yes" 
                      checked={editAttributeData.isRequired === "Yes"} 
                      onChange={() => setEditAttributeData(prev => ({ ...prev, isRequired: "Yes" }))}
                      // disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`cat-attr-radio-label ${editAttributeData.isRequired === "No" ? 'cat-attr-radio-active' : ''} ${!isUnitAndRequiredEnabled(editAttributeData.dataType) ? 'cat-attr-radio-enabled' : ''}`}>
                    <input 
                      type="radio" 
                      className="cat-attr-radio-input"
                      value="No" 
                      checked={editAttributeData.isRequired === "No"} 
                      onChange={() => setEditAttributeData(prev => ({ ...prev, isRequired: "No" }))}
                      // disabled={!isUnitAndRequiredEnabled(editAttributeData.dataType) || isSubmitting}
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="cat-attr-modal-footer">
              <button className="cat-attr-modal-cancel-btn" onClick={() => setShowEditAttributeModal(false)}>Cancel</button>
              <button 
                className="cat-attr-modal-submit-btn"
                onClick={handleEditAttributeSubmit} 
                disabled={
                  !editAttributeData.key.trim() || 
                  (editAttributeData.dataType === "DROPDOWN" && editAttributeData.dropdownOptions.length === 0) ||
                  (editAttributeData.dataType === "BOOLEAN" && !editAttributeData.isBoolean) ||
                  (editAttributeData.dataType === "DROPDOWN" && editAttributeData.unitType === "Yes" && !editAttributeData.unitValues.trim()) ||
                  isSubmitting
                }
              >
                Update Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT HEADING MODAL */}
      {showEditHeadingModal && selectedHeadingForEdit && (
        <div className="cat-attr-modal-overlay" onClick={() => setShowEditHeadingModal(false)}>
          <div className="cat-attr-modal cat-attr-edit-heading-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-modal-header">
              <h2 className="cat-attr-modal-title">Edit Heading</h2>
              <button className="cat-attr-modal-close-btn" onClick={() => setShowEditHeadingModal(false)}><X size={20} /></button>
            </div>
            <div className="cat-attr-modal-body">
              <div className="cat-attr-form-group">
                <label className="cat-attr-form-label">Heading Name <span className="cat-attr-required-star">*</span></label>
                <input
                  type="text"
                  className="cat-attr-form-input"
                  placeholder="Enter heading name"
                  value={editHeadingName}
                  onChange={(e) => setEditHeadingName(e.target.value)}
                />
              </div>
            </div>
            <div className="cat-attr-modal-footer">
              <button className="cat-attr-modal-cancel-btn" onClick={() => setShowEditHeadingModal(false)}>Cancel</button>
              <button className="cat-attr-modal-submit-btn" onClick={handleEditHeadingSubmit} disabled={!editHeadingName.trim() || isSubmitting}>
                Update Heading
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION POPUP */}
      {showDeleteConfirmPopup && attributeToDelete && (
        <div className="cat-attr-modal-overlay" onClick={handleCancelDelete}>
          <div className="cat-attr-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-delete-modal-content">
              <div className="cat-attr-delete-icon">
                <X size={48} strokeWidth={1.5} />
              </div>
              <h3 className="cat-attr-delete-title">Remove Attribute</h3>
              <p className="cat-attr-delete-message">
                Are you sure you want to remove "{attributeToDelete.key}" from this Attributes Group?
              </p>
              <div className="cat-attr-delete-actions">
                <button className="cat-attr-delete-cancel-btn" onClick={handleCancelDelete} disabled={isDeleting}>
                  Cancel
                </button>
                <button className="cat-attr-delete-confirm-btn" onClick={handleConfirmDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Yes, Remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OPERATION SUCCESS POPUP */}
      {showOperationSuccessPopup && (
        <div className="cat-attr-modal-overlay" onClick={handleCloseOperationSuccessPopup}>
          <div className="cat-attr-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-success-icon">
              <img src={varifiedIcon} alt="success" />
            </div>
            <h3 className="cat-attr-success-title">{operationSuccessTitle}</h3>
            <p className="cat-attr-success-message">{operationSuccessMessage}</p>
            <button className="cat-attr-success-btn" onClick={handleCloseOperationSuccessPopup}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* ERROR POPUP */}
      {showErrorPopup && (
        <div className="cat-attr-modal-overlay" onClick={handleCloseErrorPopup}>
          <div className="cat-attr-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cat-attr-error-icon">
              <X size={48} strokeWidth={1.5} />
            </div>
            <h3 className="cat-attr-error-title">{popupTitle}</h3>
            <p className="cat-attr-error-message">{popupMessage}</p>
            <button className="cat-attr-error-btn" onClick={handleCloseErrorPopup}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryAttributesComponent;